/**
 * Number formatting shared by every tile, table and generated sentence, so a
 * value reads identically wherever it appears — the AI summary quoting
 * "£31,450" must match the KPI card showing "£31,450".
 */

export type ValueFormat = 'currency' | 'integer' | 'percent' | 'ratio'

/** Whether a rise is good news (revenue), bad news (CPA) or neither (spend). */
export type Polarity = 'up_good' | 'down_good' | 'neutral'

export type ChangeTone = 'positive' | 'negative' | 'neutral'

export interface FormatOptions {
  currency?: string
  locale?: string
  compact?: boolean
}

const DEFAULT_LOCALE = 'en-GB'
const DEFAULT_CURRENCY = 'GBP'
const MINUS = '−'

export function formatCurrency(value: number, options: FormatOptions = {}): string {
  const { currency = DEFAULT_CURRENCY, locale = DEFAULT_LOCALE, compact = false } = options
  const whole = compact || Math.abs(value) >= 1000
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    notation: compact ? 'compact' : 'standard',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: compact ? 1 : whole ? 0 : 2,
  }).format(value)
}

/**
 * ISO 4217 currencies whose minor unit is not a hundredth: the API's own
 * `money.py` table (`_ZERO_DECIMAL` / `_THREE_DECIMAL`). A fixed table rather
 * than `Intl.NumberFormat`, which can disagree with ISO 4217 and throws for a
 * code its ICU data does not know. (The advertising report still carries its
 * own copy in `features/analytics/advertising/metrics.ts`; it can import this.)
 */
const ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'UYI', 'VND',
  'VUV', 'XAF', 'XOF', 'XPF',
])
const THREE_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND'])

/** The digits after the decimal point in `currency`'s minor unit: 2 for GBP, 0 for JPY, 3 for KWD. */
export function minorUnitExponent(currency: string): number {
  const code = currency.toUpperCase()
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0
  if (THREE_DECIMAL_CURRENCIES.has(code)) return 3
  return 2
}

/** Integer minor units of `currency` as major units, the unit every formatter here takes: 1250 GBP → 12.5. */
export function majorUnits(minor: number, currency: string): number {
  return minor / 10 ** minorUnitExponent(currency)
}

export function formatInteger(value: number, options: FormatOptions = {}): string {
  const { locale = DEFAULT_LOCALE, compact = false } = options
  return new Intl.NumberFormat(locale, {
    notation: compact ? 'compact' : 'standard',
    maximumFractionDigits: compact ? 1 : 0,
  }).format(Math.round(value))
}

/** `value` is a fraction: 0.146 → "14.6%". */
export function formatPercent(value: number, digits = 1): string {
  return `${(value * 100).toFixed(digits)}%`
}

export function formatRatio(value: number): string {
  return `${value.toFixed(2)}×`
}

export function formatValue(value: number, format: ValueFormat, options: FormatOptions = {}): string {
  switch (format) {
    case 'currency':
      return formatCurrency(value, options)
    case 'integer':
      return formatInteger(value, options)
    case 'percent':
      return formatPercent(value)
    case 'ratio':
      return formatRatio(value)
  }
}

/** Relative change as a fraction, or `null` when there is nothing to compare against. */
export function percentChange(current: number, previous: number | null | undefined): number | null {
  if (previous === null || previous === undefined || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

/** 0.123 → "+12.3%", −0.04 → "−4.0%" (a true minus sign, not a hyphen). */
export function formatChange(change: number): string {
  const sign = change > 0 ? '+' : change < 0 ? MINUS : ''
  return `${sign}${formatPercent(Math.abs(change))}`
}

export function changeTone(change: number | null, polarity: Polarity): ChangeTone {
  if (change === null || polarity === 'neutral' || Math.abs(change) < 0.0005) return 'neutral'
  const rising = change > 0
  return rising === (polarity === 'up_good') ? 'positive' : 'negative'
}

export function formatRelativeTime(from: Date | string, now: Date): string {
  const minutes = Math.floor((now.getTime() - new Date(from).getTime()) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} h ago`
  return `${Math.floor(hours / 24)} d ago`
}
